<template>
  <div class="card shadow-sm evo-cuenta-card">
    <div class="card-header evo-cuenta-header d-flex justify-content-between align-items-center flex-wrap gap-2">
      <h5 class="mb-0 text-uppercase fw-bold" style="letter-spacing:0.04em;">
        Evolución de la cuenta
      </h5>
      <button
        type="button"
        class="btn btn-link btn-sm text-decoration-none p-0 evo-cuenta-download"
        :disabled="puntosFiltrados.length === 0"
        @click="descargarCsv"
      >
        <i class="bi bi-download me-1"></i>Descargar datos
      </button>
    </div>

    <div class="card-body">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div class="btn-group btn-group-sm" role="group" aria-label="Unidad">
          <button
            type="button"
            class="btn"
            :class="modo === 'euros' ? 'btn-dark' : 'btn-outline-secondary'"
            @click="modo = 'euros'"
          >
            EN EUROS (€)
          </button>
          <button
            type="button"
            class="btn"
            :class="modo === 'pct' ? 'btn-dark' : 'btn-outline-secondary'"
            @click="modo = 'pct'"
          >
            EN PORCENTAJE (%)
          </button>
        </div>
        <div class="btn-group btn-group-sm flex-wrap" role="group" aria-label="Rango temporal">
          <button
            v-for="r in rangosFijos"
            :key="r.id"
            type="button"
            class="btn"
            :class="rangoActivo === r.id ? 'btn-dark' : 'btn-outline-secondary'"
            @click="rangoActivo = r.id"
          >
            {{ r.label }}
          </button>
          <button
            v-for="y in anosDinamicos"
            :key="'y' + y"
            type="button"
            class="btn"
            :class="rangoActivo === String(y) ? 'btn-dark' : 'btn-outline-secondary'"
            @click="rangoActivo = String(y)"
          >
            {{ y }}
          </button>
          <button
            type="button"
            class="btn"
            :class="rangoActivo === 'todo' ? 'btn-dark' : 'btn-outline-secondary'"
            @click="rangoActivo = 'todo'"
          >
            Todo
          </button>
        </div>
      </div>

      <div v-if="cargando" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
        <p class="text-muted small mt-2 mb-0">Cargando evolución de la cuenta…</p>
      </div>
      <div v-else-if="errorMsg" class="alert alert-warning small mb-0">{{ errorMsg }}</div>
      <div v-else-if="puntosFiltrados.length === 0" class="text-center text-muted py-5">
        No hay datos para el rango seleccionado.
      </div>
      <div v-else class="evo-cuenta-chart-wrap">
        <Line :data="chartData" :options="chartOptions" />
      </div>

      <div v-if="!cargando && !errorMsg && puntosFiltrados.length > 0" class="evo-cuenta-legend mt-3">
        <span><span class="dot dot-cartera"></span> Tu cartera</span>
        <span class="ms-3"><span class="dot dot-aport"></span> Aportaciones netas</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { Line } from 'vue-chartjs';
import axios from '../config/axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const RANGOS_FIJOS = [
  { id: '3m', label: '3m', meses: 3 },
  { id: '6m', label: '6m', meses: 6 },
  { id: '1a', label: '1a', meses: 12 },
  { id: '5a', label: '5a', meses: 60 }
];

const MESES_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export default {
  name: 'EvolucionCuenta',
  components: { Line },
  props: {
    activo: { type: Boolean, default: false }
  },
  setup(props) {
    const puntos = ref([]);
    const anos = ref([]);
    const cargando = ref(false);
    const errorMsg = ref('');
    const cargada = ref(false);
    const modo = ref('euros');
    const rangoActivo = ref('5a');

    const rangosFijos = RANGOS_FIJOS;

    const anosDinamicos = computed(() => {
      if (anos.value.length === 0) return [];
      const sorted = [...anos.value].sort((a, b) => b - a);
      return sorted.slice(0, 3).reverse();
    });

    const parseFecha = (iso) => {
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(y, m - 1, d);
    };

    const ultimaFecha = computed(() => {
      if (puntos.value.length === 0) return null;
      return parseFecha(puntos.value[puntos.value.length - 1].fecha);
    });

    const puntosFiltrados = computed(() => {
      if (puntos.value.length === 0) return [];
      const fin = ultimaFecha.value;
      if (!fin) return puntos.value;

      const id = rangoActivo.value;
      if (id === 'todo') return puntos.value;

      const yearNum = parseInt(id, 10);
      if (!Number.isNaN(yearNum) && String(yearNum) === id) {
        return puntos.value.filter((p) => p.fecha.startsWith(`${yearNum}-`));
      }

      const rango = RANGOS_FIJOS.find((r) => r.id === id);
      if (!rango) return puntos.value;

      const inicio = new Date(fin);
      inicio.setMonth(inicio.getMonth() - rango.meses);
      return puntos.value.filter((p) => parseFecha(p.fecha) >= inicio);
    });

    /** Reduce puntos si hay demasiados; conserva inicios de mes para el eje X */
    const puntosGrafico = computed(() => {
      const src = puntosFiltrados.value;
      const maxPts = 450;
      if (src.length <= maxPts) return src;
      const step = Math.ceil(src.length / maxPts);
      const indices = new Set([0, src.length - 1]);
      for (let i = 0; i < src.length; i++) {
        if (i % step === 0) indices.add(i);
        if (i === 0 || src[i].fecha.slice(0, 7) !== src[i - 1].fecha.slice(0, 7)) indices.add(i);
      }
      return [...indices].sort((a, b) => a - b).map((i) => src[i]);
    });

    const spanDiasGrafico = computed(() => {
      const src = puntosGrafico.value;
      if (src.length < 2) return 0;
      const t0 = parseFecha(src[0].fecha).getTime();
      const t1 = parseFecha(src[src.length - 1].fecha).getTime();
      return Math.max(1, Math.round((t1 - t0) / 86400000));
    });

    /** Índices de puntos que deben mostrar etiqueta en el eje X */
    const pickTickIndicesForAxis = (fechasIso, spanDias, maxTicks = 14) => {
      const n = fechasIso.length;
      if (n === 0) return [];
      if (n <= maxTicks) return fechasIso.map((_, i) => i);

      const indices = new Set([0, n - 1]);

      const addEvenlySpaced = (count) => {
        const step = Math.max(1, Math.floor((n - 1) / (count - 1)));
        for (let i = 0; i < n; i += step) indices.add(i);
      };

      if (spanDias <= 100) {
        addEvenlySpaced(Math.min(maxTicks, 10));
      } else if (spanDias <= 450) {
        let lastMonth = '';
        for (let i = 0; i < n; i++) {
          const ym = fechasIso[i].slice(0, 7);
          if (ym !== lastMonth) {
            indices.add(i);
            lastMonth = ym;
          }
        }
      } else if (spanDias <= 2000) {
        const seen = new Set();
        for (let i = 0; i < n; i++) {
          const d = parseFecha(fechasIso[i]);
          const key = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3)}`;
          if (!seen.has(key)) {
            seen.add(key);
            indices.add(i);
          }
        }
      } else {
        const seenYear = new Set();
        for (let i = 0; i < n; i++) {
          const y = fechasIso[i].slice(0, 4);
          if (!seenYear.has(y)) {
            seenYear.add(y);
            indices.add(i);
          }
        }
      }

      let sorted = [...indices].sort((a, b) => a - b);
      if (sorted.length > maxTicks) {
        const step = (sorted.length - 1) / (maxTicks - 1);
        const thinned = new Set([0, n - 1]);
        for (let k = 1; k < maxTicks - 1; k++) {
          thinned.add(sorted[Math.round(k * step)]);
        }
        sorted = [...thinned].sort((a, b) => a - b);
      }
      return sorted;
    };

    /** Etiquetas del eje X según el rango temporal visible */
    const formatearTickEjeX = (iso, spanDias, dataIndex, totalPoints) => {
      const d = parseFecha(iso);
      const dia = d.getDate();
      const mes = d.getMonth();
      const anio = d.getFullYear();
      const esPrimero = dataIndex === 0;
      const esUltimo = dataIndex === totalPoints - 1;
      const yy = String(anio).slice(-2);

      if (spanDias <= 100) {
        return `${dia} ${MESES_ES[mes]}`;
      }
      if (spanDias <= 450) {
        return `${MESES_ES[mes]} ${yy}`;
      }
      if (spanDias <= 2000) {
        if (esPrimero || esUltimo) return `${MESES_ES[mes]} ${anio}`;
        return `${MESES_ES[mes]} ${yy}`;
      }
      if (esPrimero || esUltimo) return `${MESES_ES[mes]} ${anio}`;
      return String(anio);
    };

    const pctSobreBasePat = (valor, basePat) =>
      basePat > 0 && Number.isFinite(valor) ? (valor / basePat) * 100 : 0;

    const valoresGrafico = computed(() => {
      const src = puntosGrafico.value;
      if (src.length === 0) return { labels: [], cartera: [], aport: [] };

      const labels = src.map((p) => p.fecha);

      if (modo.value === 'euros') {
        return {
          labels,
          cartera: src.map((p) => p.patrimonio),
          aport: src.map((p) => p.aportaciones)
        };
      }

      const basePat = src[0].patrimonio;
      const baseAport = src[0].aportaciones;
      const baseRent = src[0].rentabilidad;
      return {
        labels,
        basePat,
        baseAport,
        baseRent,
        cartera: src.map((p) => (basePat > 0 ? ((p.patrimonio / basePat) - 1) * 100 : 0)),
        aport: src.map((p) =>
          basePat > 0 ? pctSobreBasePat(p.aportaciones - baseAport, basePat) : 0
        ),
        rentPct: src.map((p) =>
          basePat > 0 ? pctSobreBasePat(p.rentabilidad - baseRent, basePat) : 0
        )
      };
    });

    const chartData = computed(() => ({
      labels: valoresGrafico.value.labels,
      datasets: [
        {
          label: 'Tu cartera',
          data: valoresGrafico.value.cartera,
          borderColor: '#0d6efd',
          backgroundColor: 'rgba(13, 110, 253, 0.08)',
          fill: false,
          tension: 0.15,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2.5,
          yAxisID: 'y',
          order: 1
        },
        {
          label: 'Aportaciones netas',
          data: valoresGrafico.value.aport,
          borderColor: '#212529',
          backgroundColor: 'transparent',
          fill: false,
          stepped: 'before',
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 3,
          borderWidth: 1.5,
          yAxisID: 'y',
          order: 2
        }
      ]
    }));

    const formatoEuro = (n) =>
      new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n);

    const formatoPct = (n) =>
      new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' %';

    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          filter: (item) => item.parsed.y != null,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0;
              const p = puntosGrafico.value[idx];
              return p ? p.fechaTexto : '';
            },
            label: () => null,
            afterBody: (items) => {
              const idx = items[0]?.dataIndex ?? 0;
              const p = puntosGrafico.value[idx];
              if (!p) return [];
              if (modo.value === 'pct') {
                const v = valoresGrafico.value;
                const basePat = v.basePat || puntosGrafico.value[0].patrimonio;
                const pctPat = basePat > 0 ? ((p.patrimonio / basePat) - 1) * 100 : 0;
                const pctAp = v.aport?.[idx] ?? 0;
                const pctRent = v.rentPct?.[idx] ?? 0;
                return [
                  `Tu cartera: ${formatoPct(pctPat)}`,
                  `Aportaciones netas: ${formatoPct(pctAp)}`,
                  `Rentabilidad: ${formatoPct(pctRent)}`
                ];
              }
              return [
                `Tu cartera: ${formatoEuro(p.patrimonio)}`,
                `Aportaciones netas: ${formatoEuro(p.aportaciones)}`,
                `Rentabilidad: ${formatoEuro(p.rentabilidad)}`
              ];
            }
          }
        }
      },
      scales: {
        x: {
          afterBuildTicks: (scale) => {
            const fechas = puntosGrafico.value.map((p) => p.fecha);
            const indices = pickTickIndicesForAxis(fechas, spanDiasGrafico.value);
            scale.ticks = indices.map((idx) => ({ value: idx }));
          },
          ticks: {
            color: '#6c757d',
            maxRotation: 0,
            autoSkip: false,
            callback(value) {
              const dataIndex = typeof value === 'number' ? value : Number(value);
              const iso = puntosGrafico.value[dataIndex]?.fecha;
              if (!iso) return '';
              return formatearTickEjeX(
                iso,
                spanDiasGrafico.value,
                dataIndex,
                puntosGrafico.value.length
              );
            }
          },
          grid: { display: false }
        },
        y: {
          ticks: {
            color: '#6c757d',
            callback: (v) => {
              if (modo.value === 'pct') return `${v.toFixed(0)}%`;
              const abs = Math.abs(v);
              if (abs >= 1000) return `${(v / 1000).toFixed(0)}k€`;
              return `${Math.round(v)}€`;
            }
          },
          grid: { color: 'rgba(0,0,0,0.06)' }
        }
      }
    }));

    const cargar = async (forzar = false) => {
      if (!forzar && cargada.value) return;
      cargando.value = true;
      errorMsg.value = '';
      try {
        const res = await axios.get('/api/evolucion/cuenta');
        puntos.value = Array.isArray(res.data.puntos) ? res.data.puntos : [];
        anos.value = Array.isArray(res.data.anos) ? res.data.anos : [];
        cargada.value = true;
        if (anos.value.length > 0 && !anos.value.includes(new Date().getFullYear())) {
          rangoActivo.value = 'todo';
        }
      } catch (err) {
        errorMsg.value = err.response?.data?.error || 'No se pudo cargar la evolución de la cuenta.';
      } finally {
        cargando.value = false;
      }
    };

    const descargarCsv = () => {
      const rows = [['fecha', 'patrimonio', 'aportaciones', 'rentabilidad']];
      puntosFiltrados.value.forEach((p) => {
        rows.push([p.fechaTexto, p.patrimonio, p.aportaciones, p.rentabilidad]);
      });
      const csv = rows.map((r) => r.join(';')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evolucion-cuenta-${rangoActivo.value}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    watch(() => props.activo, (v) => {
      if (v) cargar();
    });

    onMounted(() => {
      if (props.activo) cargar();
    });

    return {
      cargando,
      errorMsg,
      modo,
      rangoActivo,
      rangosFijos,
      anosDinamicos,
      puntosFiltrados,
      chartData,
      chartOptions,
      descargarCsv
    };
  }
};
</script>

<style scoped>
.evo-cuenta-card {
  border: none;
}
.evo-cuenta-header {
  background: #f4f6f8;
  border-bottom: 1px solid #dee2e6;
  color: #212529;
}
.evo-cuenta-download {
  color: #495057;
  font-weight: 500;
}
.evo-cuenta-chart-wrap {
  height: 380px;
  position: relative;
}
.evo-cuenta-legend {
  text-align: center;
  color: #6c757d;
  font-size: 0.85rem;
}
.evo-cuenta-legend .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
.dot-cartera {
  background: #0d6efd;
}
.dot-aport {
  background: #212529;
}
</style>
